import {useCallback, useEffect, useRef} from 'react';
import {useFloorplanContext} from "./useFloorplanContext";
import {TMat2D} from "fabric";

// The dragged card (TableOverlayCardBody) is 112×64 px, cursor snaps to center.
// Panning triggers when the pointer crosses the container edge.
// Max overshoot = half the card size (card fully outside → stop panning).
const CARD_HALF_W = 56;     // 112 / 2
const CARD_HALF_H = 32;     // 64 / 2
const PAN_SPEED_MAX = 3;    // px per frame

/**
 * Computes pan speed for one axis.
 * Returns 0 while the pointer is inside the container.
 * Once the pointer crosses outside an edge, pans at constant speed.
 * Stops when the pointer is more than `maxOvershoot` past the edge
 * (prevents panning when hovering over sidebar / mobile footer).
 */
const edgePanDelta = (pos: number, start: number, end: number, maxOvershoot: number): number => {
    if (pos < start) {
        const overshoot = start - pos;
        if (overshoot > maxOvershoot) return 0;
        return PAN_SPEED_MAX;
    }
    if (pos > end) {
        const overshoot = pos - end;
        if (overshoot > maxOvershoot) return 0;
        return -PAN_SPEED_MAX;
    }
    return 0;
};

/**
 * Auto-pans the Fabric canvas when the drag pointer crosses
 * the edges of the container.
 *
 * Saves the viewport before panning starts. On drag end the viewport
 * is restored automatically (cancelled drag). Call `commitPan()` after
 * a successful drop to keep the panned viewport instead.
 */
export const useAutoPanOnDrag = (
    isDragging: boolean,
) => {
    const {canvas, containerRef} = useFloorplanContext();

    const pointerRef = useRef<{ x: number, y: number } | null>(null);
    const rafRef = useRef<number | null>(null);
    // Skip panning until the pointer has entered the canvas at least once
    // (prevents immediate panning when drag starts from outside, e.g. sidebar)
    const hasEnteredCanvasRef = useRef(false);
    // Viewport snapshot taken before any auto-pan (null = no panning happened)
    const savedViewportRef = useRef<TMat2D | null>(null);

    useEffect(() => {
        if (!isDragging || !canvas || !containerRef.current) return;

        hasEnteredCanvasRef.current = false;
        savedViewportRef.current = null;

        const onPointerMove = (e: PointerEvent) => {
            pointerRef.current = {x: e.clientX, y: e.clientY};
        };

        const tick = () => {
            rafRef.current = requestAnimationFrame(tick);

            if (!pointerRef.current) return;

            const container = containerRef.current;
            if (!container) return;

            const vt = canvas.viewportTransform;
            if (!vt) return;

            const rect = container.getBoundingClientRect();
            const {x, y} = pointerRef.current;

            // Track whether the pointer has entered the canvas at least once
            const isInside = x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
            if (isInside) hasEnteredCanvasRef.current = true;
            if (!hasEnteredCanvasRef.current) return;

            const dx = edgePanDelta(x, rect.left, rect.right, CARD_HALF_W);
            const dy = edgePanDelta(y, rect.top, rect.bottom, CARD_HALF_H);
            if (dx === 0 && dy === 0) return;

            // Save the viewport before the first pan so we can restore on cancel
            if (!savedViewportRef.current) {
                savedViewportRef.current = [...vt] as TMat2D;
            }

            const zoomFactor = vt[0];
            canvas.setViewportTransform([zoomFactor, 0, 0, zoomFactor, vt[4] + dx, vt[5] + dy]);
            canvas.renderAll();
        };

        window.addEventListener('pointermove', onPointerMove);
        rafRef.current = requestAnimationFrame(tick);

        return () => {
            window.removeEventListener('pointermove', onPointerMove);
            if (rafRef.current !== null) {
                cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
            }
            pointerRef.current = null;

            // Restore the viewport to where it was before auto-panning started.
            // If the drop was successful, the caller should have called commitPan()
            // which clears savedViewportRef so we skip the restore.
            const saved = savedViewportRef.current;
            if (saved && canvas) {
                canvas.setViewportTransform(saved);
                canvas.renderAll();
            }
            savedViewportRef.current = null;
        };
    }, [isDragging, canvas, containerRef]);

    // Call after a successful drop to keep the auto-panned viewport
    const commitPan = useCallback(() => {
        savedViewportRef.current = null;
    }, []);

    return {commitPan};
};
